/*
 * AMQUA help menu: launcher, dialog panel (home / track / status), order lookup, Gorgias hand-off.
 * Markup and DOM contract: snippets/help-menu.liquid. Strings: #amqua-help-i18n (never hard-coded here;
 * the only literals are Gorgias' own UI label overrides and the mailto fallback address/subject).
 * Loaded with defer only when the help-menu gate is on (layout/theme.liquid).
 */
(function () {
  'use strict';

  const script = document.getElementById('amqua-help-i18n');
  const launcher = document.getElementById('amqua-help-launcher');
  if (!script || !launcher) return;

  let i18n;
  try {
    i18n = JSON.parse(script.textContent);
  } catch (e) {
    return;
  }
  if (!i18n || typeof i18n !== 'object') return;

  const wrapper = launcher.closest('.amqua-help');
  const panel = document.getElementById('amqua-help-panel');
  const scrim = document.getElementById('amqua-help-scrim');
  if (!wrapper || !panel || !scrim) return;

  const API = ((script.dataset.api || '').trim() || 'https://app.amqua.com').replace(/\/+$/, '');
  const locale = i18n.locale === 'en' ? 'en' : 'de';

  /** Absolute endpoint on the help-menu API origin. */
  function endpoint(path) {
    try {
      return new URL(path, API).toString();
    } catch (e) {
      return API + path; /* never reached with a valid https:// origin; keeps the menu alive on a bad setting */
    }
  }
  const LOOKUP_URL = endpoint('/api/help-menu/lookup');
  const EVENT_URL = endpoint('/api/help-menu/event');

  const MAILTO = 'mailto:support@amqua.com?subject=Anfrage%20amqua.com';

  /**
   * The mailto fallback href, with an optional prefilled body (the not-received message with the order number).
   * @param {string} [body]
   * @returns {string}
   */
  function mailtoHref(body) {
    return body ? MAILTO + '&body=' + encodeURIComponent(body) : MAILTO;
  }

  const GORGIAS_TIMEOUT_MS = 8000;
  const MOBILE_QUERY = '(max-width: 767.98px)';
  const FOCUSABLE_FALLBACK =
    'a[href], button:enabled, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, [tabindex]:not([tabindex^="-"])';

  /* Gorgias UI label overrides (spec-gorgias-texts.md). These are Gorgias' own labels, not menu strings. */
  const GORGIAS_TEXTS = {
    de: {
      chatTitle: 'Amqua',
      chatWithUs: 'Schreiben Sie uns',
      contactFormIntro:
        'Schreiben Sie uns – gern mit Bestellnummer. Wir melden uns persönlich, werktags innerhalb von 24 Stunden.',
      contactFormEndingMessage: 'Danke! Ihre Nachricht ist bei uns. Wir melden uns persönlich innerhalb von 24 Std.',
      contactFormAskEmail: 'Wie erreichen wir Sie?',
      contactFormAskMessage: 'Möchten Sie uns noch etwas mitteilen?',
      inputPlaceholder: 'Nachricht schreiben …',
      emailCapturePlaceholder: 'ihre@email.de',
      getRepliesByEmail: 'Antwort per E-Mail erhalten',
      offlineIntroductionText: 'Wir sind bald wieder da',
      privacyNoticePrivacyLabel: 'Datenschutz',
      contactFormAskSubject: 'Worum geht es?',
      contactFormAskSubjectOther: 'Bitte Betreff eingeben:',
      contactFormAskAdditionalMessage: 'Möchten Sie noch etwas ergänzen?',
      send: 'Senden',
      close: 'Schließen',
      subject: 'Betreff',
      emailCaptureInputLabel: 'Ihre E-Mail-Adresse',
      emailCaptureRequiredEmailPlaceholder: 'Ihre E-Mail-Adresse',
      emailCaptureRequiredMessagePlaceholder: 'Nachricht schreiben …',
      requiredEmail: 'Bitte E-Mail-Adresse angeben',
      messageSending: 'Wird gesendet …',
      thanksForReachingOut: 'Danke für Ihre Nachricht!',
      resetConversation: 'Neue Unterhaltung beginnen',
    },
    en: {
      chatTitle: 'Amqua',
      chatWithUs: 'Chat with us',
      contactFormIntro:
        'Write to us – ideally with your order number. We\'ll reply personally within 24 hours on working days.',
      contactFormEndingMessage: 'Thank you! Your message has reached us. We\'ll reply personally within 24 hours.',
      contactFormAskEmail: 'How can we contact you?',
      contactFormAskMessage: 'Is there anything else we should know?',
      inputPlaceholder: 'Type a message...',
      emailCapturePlaceholder: 'your@email.com',
      getRepliesByEmail: 'Get replies by email',
      offlineIntroductionText: 'We will be back soon',
      privacyNoticePrivacyLabel: 'Privacy',
      contactFormAskSubject: 'What can we help you with?',
      contactFormAskSubjectOther: 'Please type the subject:',
      contactFormAskAdditionalMessage: 'Would you like to add any more information?',
      send: 'Send',
      close: 'Close',
      subject: 'Subject',
      emailCaptureInputLabel: 'Leave us your email',
      emailCaptureRequiredEmailPlaceholder: 'Leave your email',
      emailCaptureRequiredMessagePlaceholder: 'Write your message',
      requiredEmail: 'Email is required',
      messageSending: 'Sending...',
      thanksForReachingOut: 'Thanks for reaching out!',
      resetConversation: 'Start a new conversation',
    },
  };

  const screens = {};
  for (const section of panel.querySelectorAll('section[data-screen]')) {
    screens[section.getAttribute('data-screen')] = section;
  }
  if (!screens.home) return;

  const launcherLabel = launcher.querySelector('.visually-hidden');
  const iconOpen = launcher.querySelector('.amqua-help-launcher__icon--open');
  const iconClose = launcher.querySelector('.amqua-help-launcher__icon--close');
  const live = document.getElementById('amqua-help-live');
  const trackForm = document.getElementById('amqua-help-track');
  const orderInput = document.getElementById('amqua-help-order');

  let isOpen = false;
  let chatOpen = false; /* the Gorgias chat window is open (tracked from our calls and Gorgias events) */
  let returnFocus = null;
  let lockedOverflow = null;
  let openBeaconSent = false;

  /* ---------- beacons ---------- */

  function beacon(event) {
    try {
      if (navigator.sendBeacon) {
        navigator.sendBeacon(EVENT_URL, JSON.stringify({ event: event, locale: locale }));
      }
    } catch (e) {
      /* analytics must never break the menu */
    }
  }

  /* ---------- focus helpers ---------- */

  function focusablesIn(container) {
    if (!container) return [];
    const list =
      typeof getFocusableElements === 'function'
        ? getFocusableElements(container)
        : Array.from(container.querySelectorAll(FOCUSABLE_FALLBACK));
    /* drop elements that are hidden (hidden attribute / display:none ancestors) or removed from the tab order */
    return list.filter((el) => el.tabIndex >= 0 && el.getClientRects().length > 0);
  }

  function visibleScreen() {
    for (const name of Object.keys(screens)) {
      if (!screens[name].hidden) return screens[name];
    }
    return screens.home;
  }

  function focusEl(el) {
    if (!el) return;
    try {
      el.focus({ preventScroll: true });
    } catch (e) {
      el.focus();
    }
  }

  /* ---------- live region ---------- */

  let liveText = '';
  let liveFrame = 0;

  /**
   * Writes a polite announcement into #amqua-help-live ('' clears it). Cleared first and set on the next frame,
   * so the same text announced twice in a row is read again.
   * @param {string} text
   */
  function announce(text) {
    liveText = text || '';
    if (!live) return;
    if (liveFrame && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(liveFrame);
    liveFrame = 0;
    live.textContent = '';
    if (!liveText) return;
    const write = () => {
      liveFrame = 0;
      live.textContent = liveText;
    };
    if (typeof requestAnimationFrame === 'function') liveFrame = requestAnimationFrame(write);
    else write();
  }

  /** Clears the live region only if it still holds this text (a newer announcement stays). */
  function unannounce(text) {
    if (liveText && liveText === text) announce('');
  }

  /* ---------- launcher state (shared by our panel and the Gorgias window) ---------- */

  /*
   * The launcher shows the × (and the launcher_close label) while our panel OR the Gorgias window is open.
   * aria-expanded describes only our panel (aria-controls points at it).
   * data-chat-open on the wrapper lets CSS hide the launcher while the Gorgias window is open.
   */
  function syncLauncher() {
    const showClose = isOpen || chatOpen;
    launcher.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    /* SVG elements have no .hidden property: use the attribute */
    if (iconOpen) iconOpen.toggleAttribute('hidden', showClose);
    if (iconClose) iconClose.toggleAttribute('hidden', !showClose);
    if (launcherLabel) {
      const text = showClose ? i18n.launcher_close : i18n.launcher_label;
      if (text) launcherLabel.textContent = text;
    }
    if (chatOpen) wrapper.setAttribute('data-chat-open', 'true');
    else wrapper.removeAttribute('data-chat-open');
  }

  function setChatOpen(value) {
    chatOpen = !!value;
    syncLauncher();
  }

  /* ---------- open / close / show ---------- */

  function open() {
    if (isOpen) return;
    isOpen = true;
    /* a new panel session: the not-found counter and the form's error state start fresh */
    notFoundCount = 0;
    clearTrackError();
    const active = document.activeElement;
    returnFocus = active && active !== document.body && !panel.contains(active) ? active : launcher;

    scrim.hidden = false;
    panel.hidden = false;
    wrapper.setAttribute('data-open', 'true');
    syncLauncher();

    if (window.matchMedia && window.matchMedia(MOBILE_QUERY).matches) {
      lockedOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }

    focusEl(focusablesIn(visibleScreen())[0] || panel);

    if (!openBeaconSent) {
      openBeaconSent = true;
      beacon('open');
    }
  }

  function close() {
    if (!isOpen) return;
    isOpen = false;
    cancelLookup(); /* a lookup still in flight must not repaint the closed panel */

    scrim.hidden = true;
    panel.hidden = true;
    wrapper.removeAttribute('data-open');
    syncLauncher();

    if (lockedOverflow !== null) {
      document.body.style.overflow = lockedOverflow;
      lockedOverflow = null;
    }

    const target = returnFocus && document.contains(returnFocus) ? returnFocus : launcher;
    returnFocus = null;
    focusEl(target);
  }

  /**
   * Shows one screen of the panel and moves focus into it (only while the panel is open).
   * @param {'home'|'track'|'status'} name
   */
  function show(name) {
    const next = screens[name];
    if (!next) return;
    for (const key of Object.keys(screens)) {
      screens[key].hidden = key !== name;
    }
    const heading = next.querySelector('h2[id]');
    if (heading) panel.setAttribute('aria-labelledby', heading.id);

    if (!isOpen) return;
    const target = name === 'track' && orderInput ? orderInput : focusablesIn(next)[0];
    focusEl(target || panel);
  }

  /* ---------- Gorgias hand-off ---------- */

  const GORGIAS_TIMEOUT_SENTINEL = {}; /* rejection value of the 8 s timer, told apart from Gorgias errors */
  let gorgiasReadyPromise = null;
  let gorgiasInited = false;
  let gorgiasTextsApplied = false;
  let gorgiasListeners = false;
  let gorgiasPending = false;
  let gorgiasTimedOut = false;

  /* Resolves after GorgiasChat.init(). Never call GorgiasChat.* before this resolved. */
  function gorgiasReady() {
    if (!gorgiasReadyPromise) {
      gorgiasReadyPromise = new Promise((resolve) => {
        if (window.GorgiasChat) {
          resolve();
          return;
        }
        window.addEventListener('gorgias-widget-loaded', () => resolve(), { once: true });
      })
        .then(() => {
          /* the GorgiasChat object can appear after the loader ran: check again here */
          if (!window.GorgiasChat || typeof window.GorgiasChat.init !== 'function') {
            throw new Error('GorgiasChat missing');
          }
          return window.GorgiasChat.init();
        })
        .then(() => {
          gorgiasInited = true;
          attachGorgiasListeners();
        });
      /* a failed attempt must not poison later clicks */
      gorgiasReadyPromise.catch(() => {
        gorgiasReadyPromise = null;
      });
    }
    return gorgiasReadyPromise;
  }

  /* Only called after init() resolved. Keeps the launcher in sync with a chat opened or closed by Gorgias itself. */
  function attachGorgiasListeners() {
    if (gorgiasListeners) return;
    gorgiasListeners = true;
    const chat = window.GorgiasChat;
    if (typeof chat.on === 'function') {
      try {
        chat.on('widget:opened', () => setChatOpen(true));
        chat.on('widget:closed', () => setChatOpen(false));
      } catch (e) {
        /* the launcher falls back to the isOpen() check on click */
      }
    }
    setChatOpen(isGorgiasOpen());
  }

  function isGorgiasOpen() {
    if (!gorgiasInited || !window.GorgiasChat || typeof window.GorgiasChat.isOpen !== 'function') return false;
    try {
      return !!window.GorgiasChat.isOpen();
    } catch (e) {
      return false;
    }
  }

  function getTriggerText(trigger) {
    if (!trigger) return '';
    const title = trigger.querySelector('.amqua-help-row__title');
    return (title || trigger).textContent;
  }

  function setTriggerText(trigger, text) {
    if (!trigger || !text) return;
    const title = trigger.querySelector('.amqua-help-row__title');
    (title || trigger).textContent = text;
  }

  /*
   * Replaces a message trigger (<button>) with a mailto link carrying the same classes and markup.
   * A prefill (e.g. "Paket nicht erhalten?") goes into the mail body.
   */
  function toMailFallback(trigger, prefill) {
    if (!trigger || !trigger.parentNode || trigger.tagName !== 'BUTTON') return;
    const hadFocus = document.activeElement === trigger;
    const link = document.createElement('a');
    link.className = trigger.className;
    if (trigger.id) link.id = trigger.id;
    link.href = mailtoHref(prefill);
    link.setAttribute('data-action', 'mailto');
    /* a hidden trigger (e.g. the track fallback row after the form was reset) must stay hidden */
    link.hidden = trigger.hidden;
    while (trigger.firstChild) link.appendChild(trigger.firstChild);

    const title = link.querySelector('.amqua-help-row__title');
    if (title) {
      title.textContent = i18n.message_fallback || '';
      const sub = link.querySelector('.amqua-help-row__sub');
      if (sub) sub.remove();
    } else {
      link.textContent = i18n.message_fallback || '';
    }
    trigger.replaceWith(link);
    if (hadFocus && isOpen) focusEl(link);
  }

  /**
   * Opens the Gorgias chat window, or turns the trigger into a mailto link after 8 s.
   * Duplicate calls while a hand-off is pending are ignored (no beacon either).
   * @param {string} [prefill] text put into the Gorgias input / the mail body (Task 9: "Paket nicht erhalten?")
   * @param {Element} [trigger] the clicked element that shows the loading / fallback state
   * @param {string} [beaconEvent] beacon sent once the call is acted upon ('message', 'not_received')
   */
  async function openGorgias(prefill, trigger, beaconEvent) {
    if (gorgiasPending) return;
    const el = trigger || document.getElementById('amqua-help-message-row');
    if (beaconEvent) beacon(beaconEvent);

    /* Gorgias already failed to load once on this page: do not make the customer wait again */
    if (gorgiasTimedOut && !gorgiasInited) {
      toMailFallback(el, prefill);
      return;
    }

    gorgiasPending = true;
    const originalText = getTriggerText(el);
    const startOpen = isOpen;
    const startScreen = visibleScreen();
    setTriggerText(el, i18n.message_loading);
    if (el) el.setAttribute('aria-busy', 'true');
    announce(i18n.message_loading);

    try {
      if (typeof window.__amquaLoadGorgias === 'function') window.__amquaLoadGorgias();
    } catch (e) {
      /* the loader is idempotent; a failure shows up as the timeout below */
    }

    let timer = null;
    try {
      await Promise.race([
        gorgiasReady(),
        new Promise((resolve, reject) => {
          timer = setTimeout(() => reject(GORGIAS_TIMEOUT_SENTINEL), GORGIAS_TIMEOUT_MS);
        }),
      ]);
      clearTimeout(timer);
      setTriggerText(el, originalText);
      if (el) el.removeAttribute('aria-busy');

      /* the customer closed the panel or moved to another screen while we waited: do not take over */
      const unchanged = isOpen === startOpen && (!isOpen || visibleScreen() === startScreen);
      if (!unchanged) return;

      const chat = window.GorgiasChat;
      if (!gorgiasTextsApplied && typeof chat.updateTexts === 'function') {
        gorgiasTextsApplied = true;
        try {
          chat.updateTexts(GORGIAS_TEXTS[locale]);
        } catch (e) {
          /* Gorgias keeps its default labels */
        }
      }

      close();
      chat.open();
      setChatOpen(true);

      if (prefill && typeof chat.setCurrentInput === 'function') {
        try {
          chat.setCurrentInput(prefill);
        } catch (e) {
          /* prefill is a convenience */
        }
      }
    } catch (err) {
      /* timeout, blocked config, init() rejection or a Gorgias error: all expected, fall back silently */
      clearTimeout(timer);
      if (err === GORGIAS_TIMEOUT_SENTINEL) gorgiasTimedOut = true;
      if (el) {
        el.removeAttribute('aria-busy');
        if (el.tagName === 'BUTTON') toMailFallback(el, prefill);
        else setTriggerText(el, originalText);
      }
    } finally {
      gorgiasPending = false;
      /* success closes the panel; a fallback link takes focus and announces itself */
      unannounce(i18n.message_loading);
    }
  }

  /* ---------- tracking ---------- */

  const ORDER_PATTERN = /^#?\d{1,7}$/; /* same rule as the API; marketplace numbers fail here without a request */
  const LOOKUP_TIMEOUT_MS = 15000;
  const COPIED_MS = 2000;
  const DATE_LOCALE = locale === 'de' ? 'de-DE' : 'en-GB';
  const KNOWN_STATUSES = ['shipped', 'out_for_delivery', 'delivered', 'preparing', 'cancelled'];

  const statusScreen = screens.status || null;
  const emailInput = document.getElementById('amqua-help-email');
  const trackError = document.getElementById('amqua-help-track-error');
  const trackSubmit = trackForm ? trackForm.querySelector('button[type="submit"]') : null;
  const trackMarketplace = trackForm ? trackForm.querySelector('.amqua-help-track__marketplace') : null;

  /*
   * Stable ids from the markup: toMailFallback() copies the id onto the mailto <a> that replaces a button,
   * so the message and not-received triggers stay findable after a Gorgias fallback.
   */
  const STATUS_MESSAGE_ID = 'amqua-help-status-message';
  const STATUS_NOT_RECEIVED_ID = 'amqua-help-status-not-received';
  const dhlLink = document.getElementById('amqua-help-status-dhl');
  const dhlDefaultHref = dhlLink ? dhlLink.getAttribute('href') : null;

  let reqId = 0; /* bumped per submit and on close(); a response for an older id is dropped */
  let notFoundCount = 0; /* not-found results in this panel session (reset in open()) */
  let lastResult = null; /* { order, trackingNumber } of the rendered status, for copy / not-received */
  let copyTimer = null;

  function makeFormat(options) {
    try {
      return new Intl.DateTimeFormat(DATE_LOCALE, Object.assign({ timeZone: 'Europe/Berlin' }, options));
    } catch (e) {
      return null;
    }
  }
  const dateFormat = makeFormat({ day: 'numeric', month: 'long' });
  const timeFormat = makeFormat({ hour: '2-digit', minute: '2-digit' });

  /** @returns {string} the formatted ISO date, or '' for null / invalid input */
  function formatIso(iso, format) {
    if (!format || typeof iso !== 'string' || !iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    try {
      return format.format(date);
    } catch (e) {
      return '';
    }
  }

  /* Replaces {date} / {time} / {order}. A function replacement, so '$' in API data is never a pattern. */
  function interpolate(template, values) {
    return String(template || '').replace(/\{(\w+)\}/g, (match, key) =>
      Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match
    );
  }

  /** API fields are untrusted: only non-empty strings count. */
  function cleanString(value) {
    return typeof value === 'string' && value.trim() ? value.trim() : null;
  }

  /** Only absolute https:// URLs may become an href. */
  function safeHttpsUrl(value) {
    const url = cleanString(value);
    if (!url || !/^https:\/\//i.test(url)) return null;
    try {
      return new URL(url).protocol === 'https:' ? url : null;
    } catch (e) {
      return null;
    }
  }

  function setHidden(el, hidden) {
    if (el) el.hidden = !!hidden;
  }

  function setText(el, text) {
    if (el) el.textContent = text || '';
  }

  function statusField(name) {
    return statusScreen ? statusScreen.querySelector('[data-field="' + name + '"]') : null;
  }

  function trackFallback() {
    /* after a Gorgias fallback this is an <a data-action="mailto"> with the same class */
    return trackForm ? trackForm.querySelector('.amqua-help-track__fallback') : null;
  }

  /* ----- track form state ----- */

  function setSubmitLabel(text) {
    if (trackSubmit && text) trackSubmit.textContent = text;
  }

  function setBusy(busy) {
    if (!trackSubmit) return;
    trackSubmit.disabled = !!busy;
    if (busy) trackSubmit.setAttribute('aria-busy', 'true');
    else trackSubmit.removeAttribute('aria-busy');
  }

  function showTrackError(text) {
    if (!trackError) return;
    trackError.textContent = text || '';
    trackError.hidden = false;
  }

  /* Back to the pristine form: no error, no red field, "Sendung anzeigen", no fallback row, no marketplace hint. */
  function clearTrackError() {
    setHidden(trackError, true);
    if (emailInput) emailInput.removeAttribute('aria-invalid');
    setSubmitLabel(i18n.submit);
    setHidden(trackFallback(), true);
    setHidden(trackMarketplace, true);
  }

  /** Not found (404, found:false, 400 or client-side invalid input). Counts towards notFoundCount. */
  function renderNotFound() {
    notFoundCount += 1;
    showTrackError(i18n.error_not_found);
    if (emailInput) emailInput.setAttribute('aria-invalid', 'true');
    setSubmitLabel(i18n.retry);
    const escalate = notFoundCount >= 2;
    setHidden(trackFallback(), !escalate);
    setHidden(trackMarketplace, !escalate);
  }

  /** 429 / network / 5xx: message under the email field plus the message button. Does not count towards notFoundCount. */
  function renderTrackError(text) {
    showTrackError(text);
    if (emailInput) emailInput.removeAttribute('aria-invalid');
    setHidden(trackFallback(), false);
    setHidden(trackMarketplace, notFoundCount < 2);
  }

  /* ----- status screen ----- */

  /**
   * Copy button state. Copied: data-copied, the check icon instead of the copy icon, "Kopiert" as its label.
   * SVG has no .hidden property: the icons use the attribute.
   * @param {Element} button
   * @param {boolean} copied
   */
  function setCopyState(button, copied) {
    if (!button) return;
    if (copied) button.setAttribute('data-copied', 'true');
    else button.removeAttribute('data-copied');
    const copyIcon = button.querySelector('.amqua-help-copy__icon--copy');
    const doneIcon = button.querySelector('.amqua-help-copy__icon--done');
    if (copyIcon) {
      if (copied) copyIcon.setAttribute('hidden', '');
      else copyIcon.removeAttribute('hidden');
    }
    if (doneIcon) {
      if (copied) doneIcon.removeAttribute('hidden');
      else doneIcon.setAttribute('hidden', '');
    }
    setText(button.querySelector('.visually-hidden'), copied ? i18n.copied : i18n.copy);
  }

  function resetCopyButton() {
    clearTimeout(copyTimer);
    copyTimer = null;
    setCopyState(statusScreen ? statusScreen.querySelector('[data-action="copy"]') : null, false);
    unannounce(i18n.copied);
  }

  /**
   * ol[data-done] drives the rail; each done <li> gets data-state="done", the last done one aria-current="step".
   * @param {Element|null} steps
   * @param {string} done '1' | '2' | '3'
   */
  function setSteps(steps, done) {
    if (!steps) return;
    steps.setAttribute('data-done', done);
    const count = Number(done);
    const items = Array.from(steps.querySelectorAll('.amqua-help-steps__item'));
    for (const [index, item] of items.entries()) {
      if (index < count) item.setAttribute('data-state', 'done');
      else item.removeAttribute('data-state');
      if (index === count - 1) item.setAttribute('aria-current', 'step');
      else item.removeAttribute('aria-current');
    }
  }

  function statusMessageButton() {
    return document.getElementById(STATUS_MESSAGE_ID);
  }

  function statusNotReceivedButton() {
    return document.getElementById(STATUS_NOT_RECEIVED_ID);
  }

  /**
   * Fills the status screen for one lookup result and shows it. Every element a status does not use is hidden
   * and every text is rewritten, so nothing from an earlier render survives. API strings only go to textContent.
   * @param {{order?: string, status?: string, placedAt?: string|null, shippedAt?: string|null,
   *   deliveredAt?: string|null, trackingNumber?: string|null, trackingUrl?: string|null,
   *   lastEvent?: {at?: string, message?: string}|null}} result
   */
  function renderStatus(result) {
    if (!statusScreen) return;
    const status = result.status;
    const order = cleanString(result.order) || '';
    const trackingNumber = cleanString(result.trackingNumber);
    const trackingUrl = safeHttpsUrl(result.trackingUrl);
    const lastEvent = result.lastEvent && typeof result.lastEvent === 'object' ? result.lastEvent : null;

    const card = statusField('card');
    const badge = statusField('badge');
    const meta = statusField('meta');
    const steps = statusScreen.querySelector('ol.amqua-help-steps');
    const trackingRow = statusField('tracking-row');
    const note = statusField('note');
    const text = statusField('text');
    const hint = statusField('hint');
    const notReceived = statusNotReceivedButton();

    /* reset to "everything hidden, nothing written" */
    lastResult = { order: order, trackingNumber: trackingNumber };
    resetCopyButton();
    setHidden(card, false);
    setText(meta, '');
    setHidden(meta, true);
    setText(statusField('tracking'), '');
    setHidden(trackingRow, true);
    setText(note, '');
    setHidden(note, true);
    setText(text, '');
    setHidden(text, true);
    if (dhlLink) {
      if (dhlDefaultHref) dhlLink.setAttribute('href', dhlDefaultHref);
      dhlLink.hidden = true;
    }
    setText(statusField('dhl-label'), i18n.track_dhl);
    if (badge) {
      badge.setAttribute('data-tone', 'green');
      setText(badge.querySelector('.amqua-help-badge__label'), '');
    }
    setHidden(notReceived, true);
    setHidden(statusMessageButton(), true);
    setText(hint, '');
    setHidden(hint, true);

    let heading = '';
    let badgeText = '';
    let tone = 'green';
    let done = '1';
    let metaText = '';
    let noteText = '';
    let dhlLabel = '';
    let dhlHref = null;
    let hintText = '';
    let showMessage = false;

    if (status === 'shipped' || status === 'out_for_delivery') {
      heading = i18n.heading_shipped;
      badgeText = i18n.status_shipped;
      done = '2';
      const date = formatIso(result.shippedAt, dateFormat);
      if (date) metaText = interpolate(i18n.meta_shipped, { date: date });
      if (trackingNumber) {
        setText(statusField('tracking'), trackingNumber);
        setHidden(trackingRow, false);
        if (trackingUrl) {
          dhlHref = trackingUrl;
          dhlLabel = i18n.track_dhl;
          hintText = i18n.dhl_hint;
        } else {
          /* no usable DHL link: the message button is the way forward */
          showMessage = true;
        }
      } else {
        showMessage = true;
      }
    } else if (status === 'delivered') {
      heading = i18n.heading_delivered;
      badgeText = i18n.status_delivered;
      done = '3';
      const deliveredAt = result.deliveredAt || (lastEvent && lastEvent.at) || null;
      const date = formatIso(deliveredAt, dateFormat);
      const time = formatIso(deliveredAt, timeFormat);
      if (date && time) metaText = interpolate(i18n.meta_delivered, { date: date, time: time });
      const eventMessage = lastEvent ? cleanString(lastEvent.message) : null;
      noteText = locale === 'de' && eventMessage ? eventMessage : i18n.delivered_generic;
      if (trackingUrl) {
        dhlHref = trackingUrl;
        dhlLabel = i18n.proof_dhl;
      }
      setHidden(notReceived, false);
      if (notReceived && notReceived.tagName === 'A') {
        /* already the mailto fallback: carry this order in the mail body */
        notReceived.href = mailtoHref(interpolate(i18n.not_received_message, { order: order }));
      }
      /* "Öffnet den Chat …" is no longer true once the button is the mailto fallback */
      if (!notReceived || notReceived.tagName !== 'A') hintText = i18n.not_received_hint;
    } else if (status === 'preparing') {
      heading = i18n.heading_preparing;
      badgeText = i18n.status_preparing;
      tone = 'ochre';
      done = '1';
      const date = formatIso(result.placedAt, dateFormat);
      if (date) metaText = interpolate(i18n.meta_placed, { date: date });
      noteText = i18n.preparing_text;
      showMessage = true;
    } else {
      /* cancelled */
      heading = i18n.heading_cancelled;
      setHidden(card, true);
      setText(text, i18n.cancelled_text);
      setHidden(text, false);
      showMessage = true;
    }

    setText(statusField('heading'), heading);
    if (badge) {
      badge.setAttribute('data-tone', tone);
      setText(badge.querySelector('.amqua-help-badge__label'), badgeText);
    }
    setText(statusField('order'), order ? (i18n.order_prefix || '') + ' ' + order : '');
    if (metaText) {
      setText(meta, metaText);
      setHidden(meta, false);
    }
    setSteps(steps, done);
    if (noteText) {
      setText(note, noteText);
      setHidden(note, false);
    }
    /* the divider + detail area only when it holds something */
    setHidden(statusField('detail'), (!trackingRow || trackingRow.hidden) && (!note || note.hidden));
    if (dhlLink && dhlHref) {
      dhlLink.setAttribute('href', dhlHref);
      setText(statusField('dhl-label'), dhlLabel);
      dhlLink.hidden = false;
    }
    if (showMessage) setHidden(statusMessageButton(), false);
    if (hintText) {
      setText(hint, hintText);
      setHidden(hint, false);
    }

    show('status');
    /* focus the heading (not Back) so screen readers announce the result; tabindex -1 keeps it out of the Tab ring */
    const headingEl = statusField('heading');
    if (isOpen && headingEl) {
      if (!headingEl.hasAttribute('tabindex')) headingEl.setAttribute('tabindex', '-1');
      focusEl(headingEl);
    }
  }

  /* ----- lookup ----- */

  /** Maps a lookup response to what the UI renders. */
  function classify(httpStatus, ok, data) {
    if (httpStatus === 429) return { kind: 'rate' };
    if (httpStatus === 404 || httpStatus === 400) return { kind: 'not_found' };
    if (ok && data && typeof data === 'object') {
      if (data.found === false) return { kind: 'not_found' };
      if (data.found === true && KNOWN_STATUSES.indexOf(data.status) !== -1) return { kind: 'found', result: data };
    }
    return { kind: 'tech' }; /* 5xx, invalid JSON, unknown shape */
  }

  /* close() calls this: the in-flight response is dropped and the button is usable again. */
  function cancelLookup() {
    reqId += 1;
    setBusy(false);
  }

  /** @param {SubmitEvent} event */
  async function submitTrack(event) {
    event.preventDefault();
    if (!orderInput || !emailInput) return;

    const order = orderInput.value.trim();
    const email = emailInput.value.trim();
    if (!ORDER_PATTERN.test(order) || !email || !emailInput.checkValidity()) {
      cancelLookup();
      renderNotFound();
      return;
    }

    const id = ++reqId;
    const focusBefore = document.activeElement;
    setBusy(true);

    let outcome;
    let timer = null;
    try {
      const controller = typeof AbortController === 'function' ? new AbortController() : null;
      if (controller) timer = setTimeout(() => controller.abort(), LOOKUP_TIMEOUT_MS);
      const response = await fetch(LOOKUP_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order: order, email: email, locale: locale }),
        signal: controller ? controller.signal : undefined,
      });
      let data = null;
      try {
        data = await response.json();
      } catch (e) {
        data = null;
      }
      outcome = classify(response.status, response.ok, data);
    } catch (e) {
      outcome = { kind: 'tech' }; /* network error, CORS, timeout */
    } finally {
      clearTimeout(timer);
    }

    /* a newer submit or close() took over: that one owns the button state */
    if (id !== reqId) return;
    setBusy(false);
    /* closed, or the customer left the form while waiting: never repaint or reopen */
    if (!isOpen || panel.hidden || visibleScreen() !== screens.track) return;

    /* disabling the focused button drops focus to <body>: put it back */
    const active = document.activeElement;
    if ((!active || active === document.body) && focusBefore && panel.contains(focusBefore)) focusEl(focusBefore);

    if (outcome.kind === 'found') {
      clearTrackError();
      renderStatus(outcome.result);
    } else if (outcome.kind === 'not_found') {
      renderNotFound();
    } else if (outcome.kind === 'rate') {
      renderTrackError(i18n.error_rate);
    } else {
      renderTrackError(i18n.error_tech);
    }
  }

  /** @param {SubmitEvent} event */
  function onTrackSubmit(event) {
    submitTrack(event).catch(() => {
      /* unexpected throw: never leave the form busy or the rejection unhandled */
      cancelLookup();
      if (isOpen && visibleScreen() === screens.track) renderTrackError(i18n.error_tech);
    });
  }

  /* editing either field takes the red state off the email field; notFoundCount is untouched */
  function onTrackInput() {
    if (emailInput) emailInput.removeAttribute('aria-invalid');
  }

  /* ----- status actions ----- */

  async function onCopy(event, el) {
    const number = lastResult && lastResult.trackingNumber;
    if (!number || !navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') return;
    try {
      await navigator.clipboard.writeText(number);
    } catch (e) {
      return; /* no permission / not focused: the number stays selectable on screen */
    }
    if (!el.isConnected) return;
    clearTimeout(copyTimer);
    setCopyState(el, true);
    announce(i18n.copied);
    copyTimer = setTimeout(resetCopyButton, COPIED_MS);
  }

  async function onNotReceived(event, el) {
    const order = lastResult ? lastResult.order : '';
    await openGorgias(interpolate(i18n.not_received_message, { order: order }), el, 'not_received');
    /* the hand-off fell back to mailto: the "opens the chat" hint is no longer true */
    const button = statusNotReceivedButton();
    if (button && button.tagName === 'A') setHidden(statusField('hint'), true);
  }

  /* ---------- events ---------- */

  /* data-action → handler(event, element). dhl and mailto are plain links and need none. */
  const actions = {
    close: () => close(),
    /* status → track (the form keeps its values), track → home */
    back: (event, el) => show(el.closest('[data-screen="status"]') ? 'track' : 'home'),
    track: () => show('track'),
    return: () => beacon('return'), /* the <a> navigates on its own */
    message: (event, el) => openGorgias('', el, 'message'),
    copy: (event, el) => onCopy(event, el),
    'not-received': (event, el) => onNotReceived(event, el),
  };

  function onLauncherClick() {
    if (isGorgiasOpen()) {
      try {
        window.GorgiasChat.close();
      } catch (e) {
        /* fall through to the icon reset */
      }
      setChatOpen(false);
      return;
    }
    /* a missed widget:closed event left the × showing: reset it and handle the click normally */
    if (chatOpen) setChatOpen(false);
    if (isOpen) close();
    else open();
  }

  wrapper.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest('#amqua-help-launcher')) {
      onLauncherClick();
      return;
    }
    if (target === scrim) {
      close();
      return;
    }
    const el = target.closest('[data-action]');
    if (!el || !wrapper.contains(el)) return;
    const handler = actions[el.getAttribute('data-action')];
    if (handler) handler(event, el);
  });

  document.addEventListener('keydown', (event) => {
    if (!isOpen) return;
    if (event.key === 'Escape' || event.key === 'Esc') {
      event.preventDefault();
      close();
      return;
    }
    if (event.key !== 'Tab') return;

    const items = focusablesIn(visibleScreen());
    if (!items.length) {
      event.preventDefault();
      focusEl(panel);
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    const inside = items.indexOf(active) !== -1;

    if (event.shiftKey) {
      if (!inside || active === first) {
        event.preventDefault();
        focusEl(last);
      }
    } else if (!inside || active === last) {
      event.preventDefault();
      focusEl(first);
    }
  });

  if (trackForm) {
    trackForm.addEventListener('submit', onTrackSubmit);
    trackForm.addEventListener('input', onTrackInput);
  }

  /* Sync the launcher with a chat Gorgias opens by itself (campaign, restored state). Failures stay silent. */
  gorgiasReady().catch(() => {});

  window.AmquaHelpMenu = { open: open, close: close, show: show };
})();
